import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ref } from "firebase/database";
import { listenDatabaseValue as onValue } from "../../../../utils/listenDatabaseValue";
import { db } from "../../../../lib/firebase";
import { useDispatchApp } from "../../../../lib/redux";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import {
  getCreativesLayerizedPath,
  startSaveCreativesLayerized,
} from "../../../../actions/imageLayers";
import { hashStringToID } from "../../../../utils/string";
import { getUniqueID } from "../../../../utils/numbers";
import {
  CANVAS_CLIPBOARD_KIND,
  consumeClipboardPasteCount,
  getCanvasClipboard,
  isCopyHotkey,
  isDeleteHotkey,
  isNativeEditableTarget,
  isPasteHotkey,
  setCanvasClipboard,
} from "../../../../features/creativeTemplates/utils/canvasClipboard";
import {
  fitImageOverlayBox,
  sizeOverlayBoxToNaturalImage,
} from "../../../../features/creativeTemplates/utils/fitImageOverlayBox";
import {
  collectTextFontAssetRefsFromLayers,
  defaultTextStyleFromCatalog,
} from "../../../../features/creativeTemplates/utils/defaultTextFont";
import { areHighlightListsEqual } from "../../../../features/creativeTemplates/utils/textHighlights";
import { areInlineReplacementListsEqual } from "../../../../features/creativeTemplates/utils/textInlineReplacements";
import {
  getImageDimensionsFromUrl,
  readImageDimensionsFromUrl,
} from "../../../../features/creativeTemplates/utils/getImageDimensionsFromFile";
import {
  LAYERS_NORMALIZED_MAX,
  LAYERS_UNDO_LIMIT,
  LAYER_TYPE,
  cloneLayers,
  createTextLayer,
  isSameLayerBox,
  isSameLayerGeometry,
  applyLayerBoxPatch,
  areLayerListsEqual,
  applyDisplayOrderToZIndex,
  layersArrayToRecord,
  layersRecordToArray,
  loadHtmlImage,
} from "../layers/layerGeometry";
import {
  nextLayerSelection,
  nextMarqueeSelection,
  resolveOperationIds,
} from "../../../../features/creativeTemplates/editor/canvasLayerSelection";

const DUPLICATE_OFFSET = 20;

const isUndoHotkey = (event) =>
  (event.ctrlKey || event.metaKey) &&
  !event.shiftKey &&
  !event.altKey &&
  (event.code === "KeyZ" || event.key?.toLowerCase() === "z");

const useImageLayers = ({
  sourceUrl,
  enabled,
  hotkeysEnabled = enabled,
  fontFamiliesRef,
  brandFontFamilyRef,
}) => {
  const dispatch = useDispatchApp();
  const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
  const [layers, setLayers] = useState([]);
  const [canvasWidth, setCanvasWidth] = useState(null);
  const [canvasHeight, setCanvasHeight] = useState(null);
  const [creationTime, setCreationTime] = useState(null);
  const [layersDirty, setLayersDirty] = useState(false);
  const [selectedLayerId, setSelectedLayerIdState] = useState(null);
  const [selectedLayerIds, setSelectedLayerIds] = useState([]);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);
  const [hasSnapshot, setHasSnapshot] = useState(false);
  const undoStackRef = useRef([]);
  const selectedLayerIdRef = useRef(null);
  const selectedLayerIdsRef = useRef([]);
  const layersDirtyRef = useRef(false);
  const layersRef = useRef([]);
  selectedLayerIdRef.current = selectedLayerId;
  selectedLayerIdsRef.current = selectedLayerIds;
  layersDirtyRef.current = layersDirty;
  layersRef.current = layers;

  const applySelection = useCallback((id, ids) => {
    const nextIDs = Array.isArray(ids) ? ids.filter(Boolean) : id ? [id] : [];
    const primary = id ?? nextIDs[nextIDs.length - 1] ?? null;
    selectedLayerIdRef.current = primary;
    selectedLayerIdsRef.current = nextIDs;
    setSelectedLayerIdState(primary);
    setSelectedLayerIds(nextIDs);
  }, []);

  const setSelectedLayerId = useCallback(
    (idOrUpdater) => {
      const next =
        typeof idOrUpdater === "function"
          ? idOrUpdater(selectedLayerIdRef.current)
          : idOrUpdater;
      applySelection(next, next ? [next] : []);
    },
    [applySelection]
  );

  const selectLayer = useCallback(
    (id, event, options = {}) => {
      const next = nextLayerSelection({
        currentIDs: selectedLayerIdsRef.current,
        id,
        event,
        preserveMulti: options.preserveMulti,
      });
      applySelection(next.primary, next.ids);
    },
    [applySelection]
  );

  const selectLayers = useCallback(
    (ids, event) => {
      const next = nextMarqueeSelection({
        currentIDs: selectedLayerIdsRef.current,
        ids,
        event,
      });
      applySelection(next.primary, next.ids);
    },
    [applySelection]
  );

  const clearUndoHistory = useCallback(() => {
    undoStackRef.current = [];
  }, []);

  const pushUndoSnapshot = useCallback((layersSnapshot) => {
    const last = undoStackRef.current[undoStackRef.current.length - 1];
    if (last && areLayerListsEqual(last.layers, layersSnapshot)) {
      return;
    }
    undoStackRef.current.push({
      layers: cloneLayers(layersSnapshot),
      selectedLayerId: selectedLayerIdRef.current,
      selectedLayerIds: selectedLayerIdsRef.current,
      layersDirty: layersDirtyRef.current,
    });
    if (undoStackRef.current.length > LAYERS_UNDO_LIMIT) {
      undoStackRef.current.shift();
    }
  }, []);

  const urlHash = useMemo(
    () => (sourceUrl ? hashStringToID(sourceUrl) : null),
    [sourceUrl]
  );

  useEffect(() => {
    if (!enabled || !advertiserID || !urlHash) {
      clearUndoHistory();
      setLayers([]);
      setHasSnapshot(false);
      setLayersDirty(false);
      setSelectedLayerId(null);
      return undefined;
    }

    setIsLoadingSnapshot(true);
    const nodeRef = ref(
      db,
      getCreativesLayerizedPath({ advertiserID, urlHash })
    );

    const unsubscribe = onValue(nodeRef, (snapshot) => {
      setIsLoadingSnapshot(false);
      if (layersDirtyRef.current) {
        return;
      }

      if (!snapshot.exists()) {
        clearUndoHistory();
        setHasSnapshot(false);
        setLayers([]);
        setCanvasWidth(null);
        setCanvasHeight(null);
        setCreationTime(null);
        setLayersDirty(false);
        return;
      }

      const data = snapshot.val() || {};
      const nextLayers = layersRecordToArray(data.layers);
      clearUndoHistory();
      setHasSnapshot(true);
      setLayers(nextLayers);
      setCanvasWidth(data.canvasWidth || null);
      setCanvasHeight(data.canvasHeight || null);
      setCreationTime(data.creationTime || null);
      setLayersDirty(false);
      setSelectedLayerId((current) => {
        if (current && nextLayers.some((layer) => layer.id === current)) {
          return current;
        }
        const firstEditable = [...nextLayers]
          .reverse()
          .find((layer) => !layer.isBackground);
        return firstEditable?.id || nextLayers[0]?.id || null;
      });
    });

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line
  }, [advertiserID, clearUndoHistory, enabled, urlHash]);

  const updateLayerBox = useCallback(
    (layerId, box) => {
      let didChange = false;
      setLayers((current) => {
        const currentLayer = current.find((layer) => layer.id === layerId);
        if (!currentLayer) return current;
        const boxHasCrop = Object.prototype.hasOwnProperty.call(
          box || {},
          "crop"
        );
        const unchanged = boxHasCrop
          ? isSameLayerBox(currentLayer, box)
          : isSameLayerGeometry(currentLayer, box);
        if (unchanged) {
          return current;
        }
        didChange = true;
        pushUndoSnapshot(current);
        return current.map((layer) => {
          if (layer.id !== layerId) return layer;
          return applyLayerBoxPatch(layer, box);
        });
      });
      if (didChange) {
        setLayersDirty(true);
      }
    },
    [pushUndoSnapshot]
  );

  const updateLayerBoxes = useCallback(
    (updates = []) => {
      if (!updates.length) return;
      let didChange = false;
      setLayers((current) => {
        const nextById = new Map(
          updates.map((item) => [item.id, item.box || item])
        );
        let changed = false;
        const nextLayers = current.map((layer) => {
          const box = nextById.get(layer.id);
          if (!box) return layer;
          const boxHasCrop = Object.prototype.hasOwnProperty.call(
            box || {},
            "crop"
          );
          const unchanged = boxHasCrop
            ? isSameLayerBox(layer, box)
            : isSameLayerGeometry(layer, box);
          if (unchanged) return layer;
          changed = true;
          return applyLayerBoxPatch(layer, box);
        });
        if (!changed) return current;
        didChange = true;
        pushUndoSnapshot(current);
        return nextLayers;
      });
      if (didChange) {
        setLayersDirty(true);
      }
    },
    [pushUndoSnapshot]
  );

  const reorderLayers = useCallback(
    (orderedDisplayIds) => {
      if (!Array.isArray(orderedDisplayIds) || orderedDisplayIds.length === 0) {
        return;
      }

      let didChange = false;
      setLayers((current) => {
        const nextLayers = applyDisplayOrderToZIndex(
          current,
          orderedDisplayIds
        );
        if (areLayerListsEqual(current, nextLayers)) {
          return current;
        }
        didChange = true;
        pushUndoSnapshot(current);
        return nextLayers;
      });
      if (didChange) {
        setLayersDirty(true);
      }
    },
    [pushUndoSnapshot]
  );

  const renameLayer = useCallback(
    (layerId, name) => {
      const nextName = String(name || "").trim() || null;
      let didChange = false;
      setLayers((current) => {
        const currentLayer = current.find((layer) => layer.id === layerId);
        if (!currentLayer) return current;
        if ((currentLayer.name || "") === (nextName || "")) {
          return current;
        }
        didChange = true;
        pushUndoSnapshot(current);
        return current.map((layer) =>
          layer.id === layerId ? { ...layer, name: nextName } : layer
        );
      });
      if (didChange) {
        setLayersDirty(true);
      }
    },
    [pushUndoSnapshot]
  );

  const persistSnapshotNow = useCallback(
    async (targetUrl) => {
      const trimmedUrl = String(targetUrl || "").trim();
      if (!trimmedUrl || !layers.length) return { ok: false };

      return dispatch(
        startSaveCreativesLayerized({
          sourceUrl: trimmedUrl,
          snapshot: {
            canvasWidth,
            canvasHeight,
            layers: layersArrayToRecord(layers),
            creationTime: creationTime || Date.now(),
            originSourceUrl: sourceUrl || null,
          },
        })
      );
    },
    [canvasHeight, canvasWidth, creationTime, dispatch, layers, sourceUrl]
  );

  const addLayersFromUrls = useCallback(
    async (urls) => {
      const validUrls = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
      if (!validUrls.length) return;

      let boardWidth = Number(canvasWidth) || 0;
      let boardHeight = Number(canvasHeight) || 0;
      if (!boardWidth || !boardHeight) {
        const sourceSize = sourceUrl
          ? await readImageDimensionsFromUrl({ url: sourceUrl })
          : { width: 1, height: 1 };
        boardWidth = sourceSize.width;
        boardHeight = sourceSize.height;
      }

      const prepared = [];
      for (const imageUrl of validUrls) {
        const image = await loadHtmlImage(imageUrl);
        prepared.push({ imageUrl, image });
      }

      let lastLayerId = null;
      setLayers((current) => {
        pushUndoSnapshot(current);
        let nextZIndex =
          current.reduce(
            (maxZ, layer) => Math.max(maxZ, Number(layer.zIndex) || 0),
            0
          ) + 1;
        const nextLayers = [...current];
        for (const { imageUrl, image } of prepared) {
          const { nw, nh } = fitImageOverlayBox({
            imageWidth: image.naturalWidth,
            imageHeight: image.naturalHeight,
            canvasWidth: boardWidth,
            canvasHeight: boardHeight,
            maxFraction: 0.4,
          });
          const width = nw * LAYERS_NORMALIZED_MAX;
          const height = nh * LAYERS_NORMALIZED_MAX;
          const layerId = `imported_${getUniqueID()}`;
          lastLayerId = layerId;
          nextLayers.push({
            id: layerId,
            type: LAYER_TYPE.IMAGE,
            name: null,
            description: null,
            url: imageUrl,
            zIndex: nextZIndex,
            isBackground: false,
            x: (LAYERS_NORMALIZED_MAX - width) / 2,
            y: (LAYERS_NORMALIZED_MAX - height) / 2,
            width,
            height,
          });
          nextZIndex += 1;
        }
        return nextLayers;
      });
      if (lastLayerId) {
        setSelectedLayerId(lastLayerId);
      }
      setLayersDirty(true);
    },
    // eslint-disable-next-line
    [canvasHeight, canvasWidth, pushUndoSnapshot, sourceUrl]
  );

  const addTextLayer = useCallback(
    ({ name } = {}) => {
      let layerId = null;
      setLayers((current) => {
        pushUndoSnapshot(current);
        const nextZIndex =
          current.reduce(
            (maxZ, layer) => Math.max(maxZ, Number(layer.zIndex) || 0),
            0
          ) + 1;
        layerId = `text_${getUniqueID()}`;
        const defaultStyle = defaultTextStyleFromCatalog({
          families: fontFamiliesRef?.current || [],
          brandFontFamily: brandFontFamilyRef?.current || "",
          existingFontAssetRefs: collectTextFontAssetRefsFromLayers({
            layers: current,
          }),
        });
        return [
          ...current,
          createTextLayer({
            id: layerId,
            name,
            zIndex: nextZIndex,
            style: defaultStyle,
          }),
        ];
      });
      if (layerId) {
        setSelectedLayerId(layerId);
        setLayersDirty(true);
      }
    },
    // eslint-disable-next-line
    [pushUndoSnapshot]
  );

  const updateLayerText = useCallback(
    (layerId, text) => {
      const nextText = String(text ?? "");
      let didChange = false;
      setLayers((current) => {
        const currentLayer = current.find((layer) => layer.id === layerId);
        if (!currentLayer) return current;
        if ((currentLayer.text || "") === nextText) {
          return current;
        }
        didChange = true;
        pushUndoSnapshot(current);
        return current.map((layer) =>
          layer.id === layerId ? { ...layer, text: nextText } : layer
        );
      });
      if (didChange) {
        setLayersDirty(true);
      }
    },
    [pushUndoSnapshot]
  );

  const updateLayerStyle = useCallback(
    (layerId, patch) => {
      if (!patch || typeof patch !== "object") return;
      let didChange = false;
      setLayers((current) => {
        const currentLayer = current.find((layer) => layer.id === layerId);
        if (!currentLayer) return current;
        const previousStyle = currentLayer.style || {};
        const nextStyle = { ...previousStyle, ...patch };
        // A null highlights/replacements patch means "drop them", not "store null".
        if (patch.highlights === null) delete nextStyle.highlights;
        if (patch.inlineReplacements === null)
          delete nextStyle.inlineReplacements;
        if (
          areHighlightListsEqual(
            previousStyle.highlights,
            nextStyle.highlights
          ) &&
          areInlineReplacementListsEqual(
            previousStyle.inlineReplacements,
            nextStyle.inlineReplacements
          ) &&
          (previousStyle.fontSize ?? "") === (nextStyle.fontSize ?? "") &&
          (previousStyle.lineHeight ?? "") === (nextStyle.lineHeight ?? "") &&
          (previousStyle.letterSpacing ?? "") ===
            (nextStyle.letterSpacing ?? "") &&
          (previousStyle.strokeWidth ?? "") === (nextStyle.strokeWidth ?? "") &&
          (previousStyle.strokeColor || "") === (nextStyle.strokeColor || "") &&
          (previousStyle.shadowColor || "") === (nextStyle.shadowColor || "") &&
          (previousStyle.shadowAngle ?? "") === (nextStyle.shadowAngle ?? "") &&
          (previousStyle.shadowDistance ?? "") ===
            (nextStyle.shadowDistance ?? "") &&
          (previousStyle.shadowBlur ?? "") === (nextStyle.shadowBlur ?? "") &&
          (previousStyle.backgroundPadding ?? "") ===
            (nextStyle.backgroundPadding ?? "") &&
          (previousStyle.backgroundRadius ?? "") ===
            (nextStyle.backgroundRadius ?? "") &&
          (previousStyle.backgroundColor || "") ===
            (nextStyle.backgroundColor || "") &&
          (previousStyle.color || "") === (nextStyle.color || "") &&
          (previousStyle.align || "") === (nextStyle.align || "") &&
          (previousStyle.orientation || "") === (nextStyle.orientation || "") &&
          (previousStyle.fontAssetRef || "") ===
            (nextStyle.fontAssetRef || "") &&
          (previousStyle.fontFaceRef || "") === (nextStyle.fontFaceRef || "") &&
          (previousStyle.fontWeight ?? "") === (nextStyle.fontWeight ?? "") &&
          (previousStyle.textStyle || "normal") ===
            (nextStyle.textStyle || "normal")
        ) {
          return current;
        }
        didChange = true;
        pushUndoSnapshot(current);
        return current.map((layer) =>
          layer.id === layerId ? { ...layer, style: nextStyle } : layer
        );
      });
      if (didChange) {
        setLayersDirty(true);
      }
    },
    [pushUndoSnapshot]
  );

  const toggleLayerLock = useCallback(
    (layerId, locked) => {
      if (!layerId) return;
      let didChange = false;
      setLayers((current) => {
        const currentLayer = current.find((layer) => layer.id === layerId);
        if (!currentLayer) return current;
        if (Boolean(currentLayer.locked) === Boolean(locked)) return current;
        didChange = true;
        pushUndoSnapshot(current);
        return current.map((layer) =>
          layer.id === layerId ? { ...layer, locked: Boolean(locked) } : layer
        );
      });
      if (didChange) {
        setLayersDirty(true);
      }
    },
    [pushUndoSnapshot]
  );

  const toggleLayerVisible = useCallback(
    (layerId, visible) => {
      if (!layerId) return;
      let didChange = false;
      setLayers((current) => {
        const currentLayer = current.find((layer) => layer.id === layerId);
        if (!currentLayer) return current;
        const nextVisible = visible !== false;
        if ((currentLayer.visible !== false) === nextVisible) return current;
        didChange = true;
        pushUndoSnapshot(current);
        return current.map((layer) =>
          layer.id === layerId ? { ...layer, visible: nextVisible } : layer
        );
      });
      if (didChange) {
        setLayersDirty(true);
      }
    },
    [pushUndoSnapshot]
  );

  const replaceLayerImage = useCallback(
    async (layerId, url, { preserveSize = false } = {}) => {
      const nextUrl = String(url || "").trim();
      if (!nextUrl) return;
      const currentLayer = layersRef.current.find(
        (layer) => layer.id === layerId
      );
      if (!currentLayer) return;
      if (currentLayer.url === nextUrl && preserveSize) return;

      let boxPatch = null;
      if (!preserveSize) {
        try {
          const dimensions = await getImageDimensionsFromUrl({ url: nextUrl });
          let boardWidth = Number(canvasWidth) || 0;
          let boardHeight = Number(canvasHeight) || 0;
          if (!boardWidth || !boardHeight) {
            const sourceSize = sourceUrl
              ? await readImageDimensionsFromUrl({ url: sourceUrl })
              : { width: 1, height: 1 };
            boardWidth = sourceSize.width;
            boardHeight = sourceSize.height;
          }
          if (
            dimensions.width > 0 &&
            dimensions.height > 0 &&
            boardWidth > 0 &&
            boardHeight > 0
          ) {
            const sized = sizeOverlayBoxToNaturalImage({
              imageWidth: dimensions.width,
              imageHeight: dimensions.height,
              canvasWidth: boardWidth,
              canvasHeight: boardHeight,
              currentNx: (Number(currentLayer.x) || 0) / LAYERS_NORMALIZED_MAX,
              currentNy: (Number(currentLayer.y) || 0) / LAYERS_NORMALIZED_MAX,
              currentNw:
                (Number(currentLayer.width) || 0) / LAYERS_NORMALIZED_MAX,
              currentNh:
                (Number(currentLayer.height) || 0) / LAYERS_NORMALIZED_MAX,
            });
            boxPatch = {
              x: sized.nx * LAYERS_NORMALIZED_MAX,
              y: sized.ny * LAYERS_NORMALIZED_MAX,
              width: sized.nw * LAYERS_NORMALIZED_MAX,
              height: sized.nh * LAYERS_NORMALIZED_MAX,
            };
          }
        } catch {
          // Keep the previous box if the image cannot be measured.
        }
      }

      let didChange = false;
      setLayers((current) => {
        const layer = current.find((item) => item.id === layerId);
        if (!layer) return current;
        if (layer.url === nextUrl && !boxPatch) return current;
        didChange = true;
        pushUndoSnapshot(current);
        return current.map((item) => {
          if (item.id !== layerId) return item;
          const next = { ...item, url: nextUrl, type: LAYER_TYPE.IMAGE };
          if (!boxPatch) return next;
          const resized = {
            ...next,
            x: boxPatch.x,
            y: boxPatch.y,
            width: boxPatch.width,
            height: boxPatch.height,
          };
          delete resized.crop;
          return resized;
        });
      });
      if (didChange) {
        setLayersDirty(true);
      }
    },
    [canvasHeight, canvasWidth, pushUndoSnapshot, sourceUrl]
  );

  const deleteLayer = useCallback(
    (layerId) => {
      const selectedIDs = selectedLayerIdsRef.current || [];
      const { ids: requested } = resolveOperationIds({
        explicitID: layerId,
        selectedIDs,
        fallbackID: selectedLayerIdRef.current,
      });
      const idSet = new Set(requested);
      if (idSet.size === 0) return false;
      let nextSelectedId = selectedLayerIdRef.current;
      let didChange = false;
      setLayers((current) => {
        const removable = new Set(
          current
            .filter((layer) => idSet.has(layer.id) && !layer.isBackground)
            .map((layer) => layer.id)
        );
        if (removable.size === 0) return current;
        didChange = true;
        pushUndoSnapshot(current);
        const remaining = current.filter((layer) => !removable.has(layer.id));
        const fallback = [...remaining]
          .reverse()
          .find((layer) => !layer.isBackground);
        nextSelectedId = fallback?.id || remaining[0]?.id || null;
        return remaining;
      });
      if (didChange) {
        const remainingIDs = selectedIDs.filter((item) => !idSet.has(item));
        if (remainingIDs.length) {
          applySelection(
            remainingIDs.includes(selectedLayerIdRef.current)
              ? selectedLayerIdRef.current
              : remainingIDs[remainingIDs.length - 1],
            remainingIDs
          );
        } else {
          applySelection(
            nextSelectedId,
            nextSelectedId ? [nextSelectedId] : []
          );
        }
        setLayersDirty(true);
      }
      return didChange;
    },
    [applySelection, pushUndoSnapshot]
  );
  const duplicateLayer = useCallback(
    (layerId) => {
      const id = layerId || selectedLayerIdRef.current;
      if (!id) return;
      let newId = null;
      setLayers((current) => {
        const source = current.find((layer) => layer.id === id);
        if (!source || source.isBackground) return current;
        pushUndoSnapshot(current);
        const maxZ = current.reduce(
          (max, layer) => Math.max(max, Number(layer.zIndex) || 0),
          0
        );
        const cloned = cloneLayers([source])[0];
        const prefix = cloned.type === LAYER_TYPE.TEXT ? "text" : "imported";
        newId = `${prefix}_${getUniqueID()}`;
        return [
          ...current,
          {
            ...cloned,
            id: newId,
            isBackground: false,
            zIndex: maxZ + 1,
            x: Number(cloned.x) + DUPLICATE_OFFSET,
            y: Number(cloned.y) + DUPLICATE_OFFSET,
          },
        ];
      });
      if (newId) {
        setSelectedLayerId(newId);
        setLayersDirty(true);
      }
    },
    // eslint-disable-next-line
    [pushUndoSnapshot]
  );

  const copyLayer = useCallback((layerId) => {
    const { ids } = resolveOperationIds({
      explicitID: layerId,
      selectedIDs: selectedLayerIdsRef.current,
      fallbackID: selectedLayerIdRef.current,
    });
    const idSet = new Set(ids);
    const sources = layersRef.current.filter(
      (layer) => idSet.has(layer.id) && !layer.isBackground
    );
    if (sources.length === 0) return false;
    const cloned = cloneLayers(sources);
    setCanvasClipboard({
      kind: CANVAS_CLIPBOARD_KIND.IMAGE_LAYER,
      layer: cloned[0],
      layers: cloned,
    });
    return true;
  }, []);

  const pasteLayer = useCallback(() => {
    const payload = getCanvasClipboard();
    if (payload?.kind !== CANVAS_CLIPBOARD_KIND.IMAGE_LAYER || !payload.layer) {
      return false;
    }
    consumeClipboardPasteCount();
    const pastedIds = [];
    setLayers((current) => {
      pushUndoSnapshot(current);
      pastedIds.length = 0;
      const maxZ = current.reduce(
        (max, layer) => Math.max(max, Number(layer.zIndex) || 0),
        0
      );
      const sources =
        Array.isArray(payload.layers) && payload.layers.length > 0
          ? payload.layers
          : [payload.layer];
      const clones = cloneLayers(sources);
      // Center the whole copied group, keeping each layer's relative offset.
      const minX = Math.min(...clones.map((layer) => Number(layer.x) || 0));
      const minY = Math.min(...clones.map((layer) => Number(layer.y) || 0));
      const maxX = Math.max(
        ...clones.map(
          (layer) => (Number(layer.x) || 0) + (Number(layer.width) || 0)
        )
      );
      const maxY = Math.max(
        ...clones.map(
          (layer) => (Number(layer.y) || 0) + (Number(layer.height) || 0)
        )
      );
      const originX = Math.max(0, (LAYERS_NORMALIZED_MAX - (maxX - minX)) / 2);
      const originY = Math.max(0, (LAYERS_NORMALIZED_MAX - (maxY - minY)) / 2);
      const pasted = clones.map((cloned, index) => {
        const prefix = cloned.type === LAYER_TYPE.TEXT ? "text" : "imported";
        const id = `${prefix}_${getUniqueID()}`;
        pastedIds.push(id);
        return {
          ...cloned,
          id,
          isBackground: false,
          zIndex: maxZ + 1 + index,
          x: originX + ((Number(cloned.x) || 0) - minX),
          y: originY + ((Number(cloned.y) || 0) - minY),
        };
      });
      return [...current, ...pasted];
    });
    if (pastedIds.length === 0) return false;
    applySelection(pastedIds[pastedIds.length - 1], pastedIds);
    setLayersDirty(true);
    return true;
    // eslint-disable-next-line
  }, [pushUndoSnapshot]);

  const undoLastAction = useCallback(() => {
    const previous = undoStackRef.current.pop();
    if (!previous) return;
    setLayers(previous.layers);
    applySelection(
      previous.selectedLayerId,
      previous.selectedLayerIds ||
        (previous.selectedLayerId ? [previous.selectedLayerId] : [])
    );
    setLayersDirty(previous.layersDirty);
  }, [applySelection]);

  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (event) => {
      if (isNativeEditableTarget(event.target)) {
        return;
      }
      if (hotkeysEnabled && isCopyHotkey(event)) {
        if (copyLayer()) {
          event.preventDefault();
        }
        return;
      }
      if (hotkeysEnabled && isPasteHotkey(event)) {
        if (pasteLayer()) {
          event.preventDefault();
        }
        return;
      }
      if (hotkeysEnabled && isDeleteHotkey(event)) {
        if (deleteLayer()) {
          event.preventDefault();
        }
        return;
      }
      if (!isUndoHotkey(event) || !undoStackRef.current.length) {
        return;
      }
      event.preventDefault();
      undoLastAction();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    copyLayer,
    deleteLayer,
    enabled,
    hotkeysEnabled,
    pasteLayer,
    undoLastAction,
  ]);

  return {
    layers,
    canvasWidth,
    canvasHeight,
    layersDirty,
    selectedLayerId,
    selectedLayerIds,
    setSelectedLayerId,
    selectLayer,
    selectLayers,
    updateLayerBox,
    updateLayerBoxes,
    addLayersFromUrls,
    addTextLayer,
    updateLayerText,
    updateLayerStyle,
    replaceLayerImage,
    deleteLayer,
    duplicateLayer,
    copyLayer,
    pasteLayer,
    reorderLayers,
    renameLayer,
    toggleLayerLock,
    toggleLayerVisible,
    persistSnapshotNow,
    hasSnapshot,
    isLoadingSnapshot,
  };
};

export default useImageLayers;
